import { useCallback, useEffect, useState } from 'react'

/**
 * Schema version of the persisted wizard state. Bump this whenever the shape of
 * the persisted payload changes so old, incompatible snapshots are discarded.
 */
const PERSIST_VERSION = 5

/**
 * Step data structure for wizard history
 */
export type WizardStepData = {
  timestamp: number
  /** Result/navigation payload produced by the step (e.g. created record). */
  data: Record<string, any>
  /** Raw form values entered by the user, used to pre-fill the step on return. */
  formValues?: Record<string, any>
}

/**
 * Wizard history structure - object with step names as keys
 */
export type WizardHistory<T extends Record<string, any>> = Record<keyof T, WizardStepData>

/**
 * Shape of the snapshot persisted to sessionStorage.
 */
type PersistedWizardState<T extends Record<string, any>> = {
  version: number
  submissionId?: string
  visitedPath: (keyof T)[]
  currentIndex: number
  stepHistory: WizardHistory<T>
}

/**
 * Return type for useWizardHistory hook
 */
export type UseWizardHistoryReturn<T extends Record<string, any>> = {
  // Current state
  currentStep: keyof T
  currentStepData: Record<string, any>

  // Navigation functions
  goToNextStep: (nextStep: keyof T, data?: Record<string, any>) => void
  goToStep: (targetStep: keyof T) => void
  goBack: () => void
  goForward: () => void

  // Navigation availability (drives the wizard arrows)
  canGoBack: boolean
  canGoForward: boolean

  // History information
  stepHistory: WizardHistory<T>
  getCompletedSteps: () => (keyof T)[]
  getAllSteps: () => (keyof T)[]
  isStepCompleted: (step: keyof T) => boolean
  isStepCurrent: (step: keyof T) => boolean
  getStepData: (step: keyof T) => Record<string, any>

  // Per-step form draft (pre-fill when navigating back/forward)
  getStepFormValues: (step: keyof T) => Record<string, any> | undefined
  setStepFormValues: (step: keyof T, values: Record<string, any>) => void

  /** Wizard journey ID (created at email confirmation, shared by every step). */
  submissionId?: string

  // Utility functions
  reset: () => void
}

/**
 * Hook to manage wizard step history with navigation capabilities.
 *
 * The wizard is a branching graph (e.g. "still owner" vs "owner changed"), NOT a
 * linear sequence. Navigation is NON-destructive: `visitedPath` keeps the whole
 * ordered list of visited steps and `currentIndex` points at the active one, so
 * the user can move backward AND forward (arrows) without losing already-entered
 * data. The forward portion is only discarded when the user actually takes a
 * DIFFERENT branch from a previous step (divergence), which keeps abandoned
 * branches out of the history.
 *
 * When `persistKey` is provided, the wizard state is mirrored to `sessionStorage`
 * under that key: it survives a page refresh (or navigation) within the SAME tab,
 * but not across tabs nor after the tab is closed. The key MUST be scoped per
 * campaign (e.g. `wizard:<slug>`) so two campaigns never share a snapshot.
 *
 * @param steps - The steps map (keys are step ids)
 * @param initialStep - The step to start on (and to fall back to on reset)
 * @param persistKey - Optional sessionStorage key enabling per-tab persistence
 */
export const useWizardHistory = <T extends Record<string, any>>(
  steps: T,
  initialStep: keyof T,
  persistKey?: string,
): UseWizardHistoryReturn<T> => {
  // Validate that initialStep exists in steps
  if (!(initialStep in steps)) {
    throw new Error(`Initial step "${String(initialStep)}" not found in steps object`)
  }

  // Get step keys as array (used only for iteration / getAllSteps, never to infer order)
  const stepKeys = Object.keys(steps) as (keyof T)[]

  /**
   * Default empty entry for a single wizard step.
   */
  const createDefaultStepEntry = (step: keyof T): WizardStepData => ({
    timestamp: step === initialStep ? Date.now() : 0,
    data: {},
    formValues: undefined,
  })

  /**
   * Build a fresh history where only the initial step carries a timestamp.
   */
  const initializeHistory = (): WizardHistory<T> => {
    const history = {} as WizardHistory<T>

    stepKeys.forEach(step => {
      history[step] = createDefaultStepEntry(step)
    })

    return history
  }

  /**
   * Ensure every declared step exists in history with a valid `data` object.
   * Guards against partial snapshots (HMR, newly added steps, older sessions).
   */
  const mergeHistoryWithDefaults = (
    partial: Partial<WizardHistory<T>> | WizardHistory<T>,
  ): WizardHistory<T> => {
    const history = {} as WizardHistory<T>

    stepKeys.forEach(step => {
      const existing = partial[step]
      history[step] = {
        ...createDefaultStepEntry(step),
        ...existing,
        data: { ...(existing?.data ?? {}) },
      }
    })

    return history
  }

  /**
   * Load a previously persisted snapshot from sessionStorage, validating it
   * against the current schema version and step shape. Returns `null` when
   * persistence is disabled, unavailable, missing, or incompatible.
   */
  const loadPersistedState = (): PersistedWizardState<T> | null => {
    if (!persistKey || typeof window === 'undefined') {
      return null
    }

    try {
      const raw = window.sessionStorage.getItem(persistKey)
      if (!raw) {
        return null
      }

      const parsed = JSON.parse(raw) as PersistedWizardState<T>
      const knownKeys = stepKeys.map(String)

      // Invalidate on schema bump or when the persisted steps no longer match.
      const isCompatible =
        parsed.version === PERSIST_VERSION &&
        Array.isArray(parsed.visitedPath) &&
        parsed.visitedPath.length > 0 &&
        parsed.visitedPath.every(step => knownKeys.includes(String(step))) &&
        knownKeys.every(step => parsed.stepHistory?.[step as keyof T] != null) &&
        typeof parsed.currentIndex === 'number' &&
        parsed.currentIndex >= 0 &&
        parsed.currentIndex < parsed.visitedPath.length

      return isCompatible ? parsed : null
    } catch (error) {
      console.error('Error restoring wizard history from sessionStorage:', error)
      return null
    }
  }

  // Read the persisted snapshot once (lazy initializer → no read on every render).
  const [persisted] = useState<PersistedWizardState<T> | null>(loadPersistedState)

  // Full ordered list of visited steps (may extend beyond the current step after
  // navigating backward). The active step is `visitedPath[currentIndex]`.
  const [visitedPath, setVisitedPath] = useState<(keyof T)[]>(
    () => persisted?.visitedPath ?? [initialStep],
  )

  // Index of the active step within `visitedPath`.
  const [currentIndex, setCurrentIndex] = useState<number>(() => persisted?.currentIndex ?? 0)

  // History of all steps with their data. Merge over a fresh history so any newly
  // added step (absent from an older snapshot) still has a default entry.
  const [stepHistory, setStepHistory] = useState<WizardHistory<T>>(() =>
    persisted
      ? mergeHistoryWithDefaults(persisted.stepHistory)
      : initializeHistory(),
  )

  const [submissionId, setSubmissionId] = useState<string | undefined>(
    () => persisted?.submissionId,
  )

  // Active step and its produced data are derived from the path + index.
  const currentStep = visitedPath[currentIndex]
  const currentStepData = stepHistory[currentStep]?.data ?? {}

  // Mirror the wizard state to sessionStorage on every change (per-tab refresh).
  useEffect(() => {
    if (!persistKey || typeof window === 'undefined') {
      return
    }

    try {
      const snapshot: PersistedWizardState<T> = {
        version: PERSIST_VERSION,
        submissionId,
        visitedPath,
        currentIndex,
        stepHistory,
      }
      window.sessionStorage.setItem(persistKey, JSON.stringify(snapshot))
    } catch (error) {
      console.error('Error saving wizard history to sessionStorage:', error)
    }
  }, [persistKey, visitedPath, currentIndex, stepHistory, submissionId])

  /**
   * Navigate forward to the next step.
   *
   * If the next step matches the one already recorded right after the current
   * index, we simply advance and KEEP the forward portion (the user is redoing
   * the same branch). Otherwise we truncate the forward portion (a different
   * branch is taken) and reset the dropped steps so stale data never lingers.
   *
   * @param nextStep - The step to move to
   * @param data - Result/navigation payload produced by the current (source) step
   */
  const goToNextStep = useCallback(
    (nextStep: keyof T, data?: Record<string, any>) => {
      if (!(nextStep in steps)) {
        throw new Error(`Step "${String(nextStep)}" not found in steps object`)
      }

      const sourceStep = visitedPath[currentIndex]
      const followStep = visitedPath[currentIndex + 1]
      const continuing = followStep === nextStep

      const newPath = continuing
        ? visitedPath
        : [...visitedPath.slice(0, currentIndex + 1), nextStep]
      const droppedSteps = continuing ? [] : visitedPath.slice(currentIndex + 1)

      setStepHistory(prev => {
        const next = mergeHistoryWithDefaults(prev)

        // Persist the data produced by the source step on the source step.
        if (data !== undefined) {
          next[sourceStep] = {
            ...next[sourceStep],
            data,
          }
        }

        // Reset steps dropped from an abandoned forward branch.
        droppedSteps.forEach(step => {
          next[step] = createDefaultStepEntry(step)
        })

        // Seed/merge the next step's data and refresh its timestamp.
        const nextStepEntry = next[nextStep]
        next[nextStep] = {
          ...nextStepEntry,
          timestamp: Date.now(),
          data: { ...nextStepEntry.data, ...(data ?? {}) },
        }

        return next
      })

      setVisitedPath(newPath)
      setCurrentIndex(currentIndex + 1)

      if (data?.submissionId) {
        setSubmissionId(String(data.submissionId))
      }
    },
    [currentIndex, visitedPath, steps],
  )

  /**
   * Navigate to a specific step already present on the visit path. This is
   * non-destructive: the forward portion is preserved so the user can come back.
   */
  const goToStep = useCallback(
    (targetStep: keyof T) => {
      if (!(targetStep in steps)) {
        throw new Error(`Step "${String(targetStep)}" not found in steps object`)
      }

      const targetIndex = visitedPath.indexOf(targetStep)
      if (targetIndex >= 0) {
        setCurrentIndex(targetIndex)
      }
    },
    [visitedPath, steps],
  )

  /**
   * Go back to the previous step on the visit path (non-destructive).
   */
  const goBack = useCallback(() => {
    setCurrentIndex(index => (index > 0 ? index - 1 : index))
  }, [])

  /**
   * Go forward to the next already-visited step on the visit path.
   */
  const goForward = useCallback(() => {
    setCurrentIndex(index => (index < visitedPath.length - 1 ? index + 1 : index))
  }, [visitedPath.length])

  // Navigation availability for the wizard arrows.
  const canGoBack = currentIndex > 0
  const canGoForward = currentIndex < visitedPath.length - 1

  /**
   * Get all completed steps (everything strictly before the current one).
   */
  const getCompletedSteps = useCallback(() => {
    return visitedPath.slice(0, currentIndex)
  }, [visitedPath, currentIndex])

  /**
   * Get all known steps (declaration order). Order is irrelevant to navigation.
   */
  const getAllSteps = useCallback(() => {
    return stepKeys
  }, [stepKeys])

  /**
   * Check if a step has been visited and sits before the current one.
   */
  const isStepCompleted = useCallback(
    (step: keyof T) => {
      const index = visitedPath.indexOf(step)
      return index >= 0 && index < currentIndex
    },
    [visitedPath, currentIndex],
  )

  /**
   * Check if a step is the current one.
   */
  const isStepCurrent = useCallback(
    (step: keyof T) => {
      return step === currentStep
    },
    [currentStep],
  )

  /**
   * Get the produced data for a specific step.
   */
  const getStepData = useCallback(
    (step: keyof T) => {
      return stepHistory[step]?.data || {}
    },
    [stepHistory],
  )

  /**
   * Get the raw form values previously entered on a step (for pre-fill).
   */
  const getStepFormValues = useCallback(
    (step: keyof T) => {
      return stepHistory[step]?.formValues
    },
    [stepHistory],
  )

  /**
   * Store the raw form values entered on a step so they can pre-fill the form
   * when the user navigates back (or forward) to it.
   */
  const setStepFormValues = useCallback((step: keyof T, values: Record<string, any>) => {
    setStepHistory(prev => {
      const next = mergeHistoryWithDefaults(prev)
      next[step] = { ...next[step], formValues: values }
      return next
    })
  }, [])

  /**
   * Reset the wizard to its initial state and clear any persisted snapshot.
   */
  const reset = useCallback(() => {
    setVisitedPath([initialStep])
    setCurrentIndex(0)
    setStepHistory(initializeHistory())
    setSubmissionId(undefined)

    if (persistKey && typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(persistKey)
      } catch (error) {
        console.error('Error clearing wizard history from sessionStorage:', error)
      }
    }
  }, [initialStep, persistKey])

  return {
    // Current state
    currentStep,
    currentStepData,

    // Navigation functions
    goToNextStep,
    goToStep,
    goBack,
    goForward,

    // Navigation availability
    canGoBack,
    canGoForward,

    // History information
    stepHistory,
    getCompletedSteps,
    getAllSteps,
    isStepCompleted,
    isStepCurrent,
    getStepData,

    // Per-step form draft
    getStepFormValues,
    setStepFormValues,

    submissionId,

    // Utility functions
    reset,
  }
}
